import React, { Fragment, memo, useCallback, useMemo } from 'react'
import Menu from '@mui/material/Menu'
import { SxProps, Theme } from '@mui/material'
import { CalendarMajor, CancelMinor, CaretDownMinor, CaretUpMinor } from '@shopify/polaris-icons'
import {
  DateRangeCalendar,
  DateRangeCalendarProps,
} from '@mui/x-date-pickers-pro/DateRangeCalendar'
import { DateRange, RangePosition } from '@mui/x-date-pickers-pro'
import { Dayjs } from 'dayjs'
import tw from 'twin.macro'
import { colors } from 'src/theme'
import { Button, contentColors } from '../Button'

const DEFAULT_FORMAT = 'MMM D, YYYY'

// Radius of the black selected day; band caps use a smaller radius so the grey
// range strip reads as a soft rectangle rather than a pill.
const DAY_RADIUS = '8px'
const BAND_RADIUS = '4px'

// Range calendar restyle (see Figma): dark selected day, grey range band, and
// rounded days/hover instead of the MUI-default purple circles.
const calendarSx: SxProps<Theme> = {
  // Skip empty month-edge filler cells (MUI leaves their bg blank too), so the
  // range band never bleeds into the gaps of an adjacent month.
  '& .MuiDateRangePickerDay-rangeIntervalDayHighlight:not(.MuiDateRangePickerDay-hiddenDayFiller):not(.MuiDateRangePickerDay-outsideCurrentMonth)':
    {
      backgroundColor: colors.SHADES_GREY_50,
    },
  // Band caps — MUI rounds these to 50% (pill); override every trigger it uses
  // (range start/end, each week-row edge, and the grid's first/last cell).
  '& .MuiDateRangePickerDay-rangeIntervalDayHighlight:first-of-type, & .MuiDateRangePickerDay-firstVisibleCell, & .MuiDateRangePickerDay-rangeIntervalDayHighlightStart':
    {
      borderTopLeftRadius: BAND_RADIUS,
      borderBottomLeftRadius: BAND_RADIUS,
    },
  '& .MuiDateRangePickerDay-rangeIntervalDayHighlight:last-of-type, & .MuiDateRangePickerDay-lastVisibleCell, & .MuiDateRangePickerDay-rangeIntervalDayHighlightEnd':
    {
      borderTopRightRadius: BAND_RADIUS,
      borderBottomRightRadius: BAND_RADIUS,
    },
  '& .MuiPickersDay-root': {
    borderRadius: DAY_RADIUS,
    color: colors.SHADES_GREY_800,
    '&:hover, &:focus': {
      backgroundColor: colors.SHADES_GREY_50,
      boxShadow: `inset 0 0 0 1px ${colors.SHADES_GREY_200}`,
    },
    '&.MuiPickersDay-today': {
      border: 'none',
      backgroundColor: colors.SHADES_GREY_50,
      fontWeight: 600,
    },
    '&.Mui-selected': {
      backgroundColor: colors.SHADES_GREY_900,
      color: colors.GRAY_WHITE,
      fontWeight: 400,
      '&:hover, &:focus': {
        backgroundColor: colors.SHADES_GREY_900,
        boxShadow: 'none',
      },
    },
  },
}

interface Props extends Omit<DateRangeCalendarProps<Dayjs>, 'value' | 'onChange'> {
  title: string
  value: DateRange<Dayjs>
  onChange: (value: DateRange<Dayjs>) => void
  onClear?: () => void
  format?: string
  disabled?: boolean
}

const BasicDateRangePickerComponent = ({
  title,
  value,
  onChange,
  onClear,
  format = DEFAULT_FORMAT,
  disabled,
  calendars = 2,
  ...props
}: Props) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [rangePosition, setRangePosition] = React.useState<RangePosition>('start')

  const isMenuOpen = Boolean(anchorEl)

  const [start, end] = value
  const hasRange = Boolean(start && end)
  const hasSelectedDate = Boolean(start || end)
  const showClearButton = hasSelectedDate && !isMenuOpen

  const handleOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setRangePosition('start')
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleChange = useCallback(
    (newValue: DateRange<Dayjs>) => {
      // Selecting a start always begins a fresh range, so drop any previous end.
      if (rangePosition === 'start') {
        onChange([newValue[0], null])

        return
      }

      onChange(newValue)

      if (newValue[0] && newValue[1]) {
        handleClose()
      }
    },
    [rangePosition, onChange, handleClose],
  )

  const handleClear = useCallback(() => {
    onClear?.()
  }, [onClear])

  const label = useMemo(() => {
    if (hasRange) {
      return `${start?.format(format)} - ${end?.format(format)}`
    }

    if (start) {
      return `${start.format(format)} -`
    }

    if (end) {
      return `- ${end.format(format)}`
    }

    return title
  }, [hasRange, start, end, format, title])

  const rightIcon = useMemo(() => {
    if (isMenuOpen) {
      return <CaretUpMinor width={20} fill={contentColors.white} />
    }

    if (hasSelectedDate) {
      return null
    }

    return <CaretDownMinor width={20} fill={contentColors.white} />
  }, [isMenuOpen, hasSelectedDate])

  return (
    <Fragment>
      <div css={tw`flex items-center`}>
        <Button
          color="white"
          size="small"
          selected={isMenuOpen}
          disabled={disabled}
          onClick={handleOpen}
          leftIcon={<CalendarMajor width={20} fill={contentColors.white} />}
          rightIcon={rightIcon}
          className={[tw`px-3`, showClearButton && tw`rounded-r-none border-r-0`]}
        >
          {label}
        </Button>
        {showClearButton && (
          <Button
            color="white"
            size="small"
            iconOnly
            disabled={disabled}
            aria-label="Clear date range"
            onClick={handleClear}
            leftIcon={<CancelMinor width={20} fill={contentColors.white} />}
            className={tw`rounded-l-none`}
          />
        )}
      </div>
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { mt: 1 } }}
      >
        <DateRangeCalendar
          {...props}
          sx={calendarSx}
          calendars={calendars}
          value={value}
          onChange={handleChange}
          rangePosition={rangePosition}
          onRangePositionChange={setRangePosition}
        />
      </Menu>
    </Fragment>
  )
}

export const BasicDateRangePicker = memo(BasicDateRangePickerComponent)
